import { useCallback, useEffect, useRef, useState } from 'react';

import useScanLookup from '#/hooks/scan/useScanLookup';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import {
  getStockNoItemId,
  lookupStockNo,
} from '#/modules/imsMgmt/receipts/shared/scan';
import StockScanAmbiguousDialog from '#/modules/imsMgmt/receipts/stocktake/components/StockScanAmbiguousDialog';
import StockScanCoverOverlay from '#/modules/imsMgmt/receipts/stocktake/components/StockScanCoverOverlay';
import {
  buildScanNoticeFieldsFromLine,
  buildScanNoticeFieldsFromRow,
  findExistingScanLine,
} from '#/modules/imsMgmt/receipts/stocktake/helper/buildScanNoticeFields';
import {
  mapInventoryStockToScanLine,
} from '#/modules/imsMgmt/receipts/stocktake/helper/mapInventoryStockToScanLine';
import { playStocktakeScanSound } from '#/modules/imsMgmt/receipts/stocktake/helper/playStocktakeScanSound';
import { getRequestTransportErrorMessage } from '#/utils/requestError';

function collectExistingInvStockIds(lines) {
  return new Set(
    (Array.isArray(lines) ? lines : [])
      .map((line) => line?.invStockId || line?.inventoryStockId)
      .filter(Boolean),
  );
}

function collectExistingStockNos(lines) {
  return new Set(
    (Array.isArray(lines) ? lines : [])
      .map((line) => String(line?.stockNo ?? '').trim())
      .filter(Boolean),
  );
}

/**
 * 盤點／掃碼：包 useScanLookup，保留 notice／音／高亮／inline。
 */
function useInventoryStockScan({
  lines = [],
  onAppendLine,
  scanMode = 'stockNo',
} = {}) {
  const [scanNotice, setScanNotice] = useState(null);
  const [highlightInvStockId, setHighlightInvStockId] = useState(null);
  const [ambiguousOpen, setAmbiguousOpen] = useState(false);
  const [ambiguousCandidates, setAmbiguousCandidates] = useState([]);
  const [ambiguousSession, setAmbiguousSession] = useState(0);

  const linesRef = useRef(lines);
  const onAppendLineRef = useRef(onAppendLine);
  const scanModeRef = useRef(scanMode);
  const ambiguousResolveRef = useRef(null);
  const { confirm, ConfirmDialogHost } = useConfirmDialog();

  useEffect(() => {
    linesRef.current = lines;
    onAppendLineRef.current = onAppendLine;
    scanModeRef.current = scanMode;
  });

  function clearScanNotice() {
    setScanNotice(null);
  }

  const finishAmbiguous = useCallback((rowOrNull) => {
    if (!ambiguousResolveRef.current) {
      setAmbiguousOpen(false);
      return;
    }
    const resolve = ambiguousResolveRef.current;
    ambiguousResolveRef.current = null;
    setAmbiguousOpen(false);
    setAmbiguousCandidates([]);
    resolve(rowOrNull);
  }, []);

  const openAmbiguousDialog = useCallback((candidates) => {
    return new Promise((resolve) => {
      ambiguousResolveRef.current = resolve;
      setAmbiguousCandidates(Array.isArray(candidates) ? candidates : []);
      setAmbiguousSession((prev) => prev + 1);
      setAmbiguousOpen(true);
    });
  }, []);

  function appendMappedLine(line) {
    if (!line || typeof onAppendLineRef.current !== 'function') {
      return false;
    }
    onAppendLineRef.current(line);
    setScanNotice({
      kind: 'success',
      ...buildScanNoticeFieldsFromLine(line),
    });
    setHighlightInvStockId(line.invStockId || line.inventoryStockId || null);
    return true;
  }

  function appendStockedAndClear(row) {
    return appendMappedLine(mapInventoryStockToScanLine(row));
  }

  function setDuplicateNotice(stockNo, context = {}) {
    playStocktakeScanSound('duplicate');
    setHighlightInvStockId(null);

    let fields;
    if (context.row) {
      fields = buildScanNoticeFieldsFromRow(context.row, stockNo);
    } else {
      const existing = findExistingScanLine(linesRef.current, {
        invStockId: context.invStockId,
        stockNo,
      });
      fields = existing
        ? buildScanNoticeFieldsFromLine(existing, stockNo)
        : {
          stockNo: String(stockNo ?? '').trim(),
          mtrNo: '',
          mtrName: '',
          qty: '',
        };
    }

    setScanNotice({
      kind: 'duplicate',
      ...fields,
    });
  }

  function setNotFoundNotice(stockNo) {
    playStocktakeScanSound('notFound');
    setHighlightInvStockId(null);
    setScanNotice({
      kind: 'notFound',
      stockNo: String(stockNo ?? '').trim(),
      mtrNo: '',
      mtrName: '',
      qty: '',
    });
  }

  const {
    scanCode,
    setScanCode,
    isLookingUp,
    inputRef,
    runLookup,
  } = useScanLookup({
    lookup: (code) => lookupStockNo(code, { mode: scanModeRef.current }),
    onEmpty: (code) => {
      const existingStockNos = collectExistingStockNos(linesRef.current);
      if (existingStockNos.has(code)) {
        setDuplicateNotice(code);
        return;
      }
      setNotFoundNotice(code);
    },
    onPick: (row) => {
      const existingInvStockIds = collectExistingInvStockIds(linesRef.current);
      const itemId = getStockNoItemId(row);
      if (itemId && existingInvStockIds.has(itemId)) {
        setDuplicateNotice(row.stockNo || '', {
          row,
          invStockId: itemId,
        });
        return;
      }
      appendStockedAndClear(row);
    },
    onError: async (error) => {
      const transportMessage = getRequestTransportErrorMessage(error);
      await confirm({
        title: '查詢失敗',
        message: transportMessage
          || error?.response?.data?.message
          || error?.message
          || '查詢庫存失敗，請稍後再試',
        showCancel: false,
        confirmText: '確認',
        confirmOnEnterSpace: true,
      });
    },
    resolveAmbiguous: (candidates) => openAmbiguousDialog(candidates),
  });

  const AmbiguousDialogHost = useCallback(function AmbiguousDialogHost() {
    return (
      <StockScanAmbiguousDialog
        key={ambiguousSession}
        open={ambiguousOpen}
        candidates={ambiguousCandidates}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            finishAmbiguous(null);
            return;
          }
          setAmbiguousOpen(true);
        }}
        onConfirm={(row) => finishAmbiguous(row)}
        onCancel={() => finishAmbiguous(null)}
      />
    );
  }, [
    ambiguousCandidates,
    ambiguousOpen,
    ambiguousSession,
    finishAmbiguous,
  ]);

  const CoverOverlayHost = useCallback(function CoverOverlayHost() {
    return <StockScanCoverOverlay show={isLookingUp} />;
  }, [isLookingUp]);

  return {
    scanCode,
    setScanCode,
    onScanEnter: runLookup,
    isScanning: isLookingUp,
    inputRef,
    scanNotice,
    clearScanNotice,
    highlightInvStockId,
    ConfirmDialogHost,
    AmbiguousDialogHost,
    CoverOverlayHost,
  };
}

export default useInventoryStockScan;
